import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const TABS = ['End-to-End Tracking', 'Visa Permits', 'Work & Resident Permits', 'Summary Sheet'];

const Immigration = () => {
  const [activeTab, setActiveTab] = useState('End-to-End Tracking');
  const [data, setData] = useState([
    { id: 'A0001', name: 'Sung Kyaw Hun', nationality: 'Myanmar', client: 'Tullow', date: '2025-02-01', status: 'Complete' },
    { id: 'A0002', name: 'Mohammed Zanoray', nationality: 'Indonesia', client: 'ENI', date: '2025-02-05', status: 'In-Progress' },
  ]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', nationality: '', client: 'Tullow' });

  const handleCreate = (e) => {
    e.preventDefault();
    const newApp = {
      id: `A00${data.length + 50}`,
      name: formData.name,
      nationality: formData.nationality,
      client: formData.client,
      date: new Date().toISOString().split('T')[0],
      status: 'Pending'
    };
    setData([newApp, ...data]);
    setShowModal(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Immigration Tracking</h1>
          <p className="text-gray-500 text-sm">Regulatory compliance and logistics monitoring.</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-[#11469A] text-white px-5 py-2.5 rounded-lg font-bold text-sm shadow-sm hover:bg-[#0d3678] flex items-center gap-2"
        >
          <SafeIcon name="Plus" size={18} /> New Application
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex overflow-x-auto border-b border-gray-100 scrollbar-hide">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-4 text-sm font-bold whitespace-nowrap relative ${
                activeTab === tab ? 'text-[#11469A]' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#11469A]" />}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-left text-xs whitespace-nowrap">
            <thead className="bg-[#FFD966] text-gray-900 font-bold border-b-2 border-gray-300">
              <tr>
                <th className="px-4 py-3 border-r border-gray-300/50">App ID</th>
                <th className="px-4 py-3 border-r border-gray-300/50">Applicant Name</th>
                <th className="px-4 py-3 border-r border-gray-300/50">Nationality</th>
                <th className="px-4 py-3 border-r border-gray-300/50">Client</th>
                <th className="px-4 py-3 border-r border-gray-300/50">Date</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map(row => (
                <tr key={row.id} className="hover:bg-blue-50/20 transition-colors">
                  <td className="px-4 py-3 border-r border-gray-100 font-bold">{row.id}</td>
                  <td className="px-4 py-3 border-r border-gray-100 font-bold text-[#11469A]">{row.name}</td>
                  <td className="px-4 py-3 border-r border-gray-100">{row.nationality}</td>
                  <td className="px-4 py-3 border-r border-gray-100">{row.client}</td>
                  <td className="px-4 py-3 border-r border-gray-100">{row.date}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${
                      row.status === 'Complete' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-bold text-[#11469A]">New Immigration Case</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><SafeIcon name="X" size={20} /></button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Applicant Name</label>
                  <input required type="text" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] outline-none text-sm" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Nationality</label>
                  <input required type="text" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] outline-none text-sm" value={formData.nationality} onChange={e => setFormData({...formData, nationality: e.target.value})} />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Client Assigned</label>
                  <select className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] outline-none text-sm" value={formData.client} onChange={e => setFormData({...formData, client: e.target.value})}>
                    <option>Tullow</option>
                    <option>ENI</option>
                    <option>Kosmos</option>
                    <option>MODEC</option>
                  </select>
                </div>
                <div className="pt-4 flex gap-3">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex-1 py-3 bg-[#11469A] text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-[#0d3678]">Start Process</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Immigration;